import { useState, useEffect } from "react";
import { Zap, Brain, Shield, Eye, Clock, BarChart3 } from "lucide-react";
import { useLeadSubmit } from "../../hooks/useLeadSubmit";
import { calculateAssessmentROI } from "../../lib/roiCalculator";
import { generateAssessmentPDF, getAssessmentPDFBase64 } from "../../lib/generateAssessmentPDF";
import { emailReport } from "../../lib/emailReport";
import axios from "axios";
import { getSteps, getScoreLabel } from "./assessmentData";
import AssessmentIntro from "./AssessmentIntro";
import AssessmentSteps from "./AssessmentSteps";
import AssessmentContact from "./AssessmentContact";
import AssessmentResults from "./AssessmentResults";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pull Human Risk Score from simulation (localStorage)
function getHumanRiskScore() {
  try {
    const raw = localStorage.getItem("veracity_hri");
    if (!raw) return null;
    const data = JSON.parse(raw);
    const scores = Object.values(data.highScores || {});
    return scores.length > 0 ? Math.max(...scores) : null;
  } catch (e) { return null; }
}

export default function FreeAuditOffer() {
  const { submitted, submitting, error, leadId, reportToken, submitLead } = useLeadSubmit();
  const [stage, setStage] = useState("intro"); // intro, assess, contact, results
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [animating, setAnimating] = useState(false);
  const [contactInfo, setContactInfo] = useState({ company: "", name: "", phone: "", email: "" });
  const [completedCount, setCompletedCount] = useState(0);
  const [emailingReport, setEmailingReport] = useState(false);
  const [reportEmailStatus, setReportEmailStatus] = useState(null); // null, "sent", "error"

  useEffect(() => {
    axios.get(`${API}/stats/assessments-completed`)
      .then((res) => setCompletedCount(res.data?.count || 0))
      .catch((err) => console.error("Failed to load assessment completion count:", err));
  }, []);

  const steps = getSteps(answers);

  // Calculate category scores
  const calcCategoryScore = (categoryQuestions) => {
    const scored = categoryQuestions.filter(q => q.options[0]?.score !== undefined);
    if (scored.length === 0) return 100;
    const total = scored.reduce((sum, q) => sum + (answers[q.id]?.score ?? 10), 0);
    const worst = scored.length * 10;
    return Math.round(((worst - total) / worst) * 100);
  };

  const operationalScore = calcCategoryScore(steps[1].questions);
  const automationScore = calcCategoryScore(steps[2].questions);
  const aiScore = calcCategoryScore(steps[3].questions);
  const accessScore = calcCategoryScore(steps[4].questions);
  const continuityScore = calcCategoryScore(steps[5].questions);
  const riskScore = Math.round((accessScore + continuityScore) / 2);

  const humanRiskScore = getHumanRiskScore();

  // Overall = weighted average of all available scores
  const allScores = [automationScore, aiScore, riskScore, operationalScore, continuityScore];
  if (humanRiskScore !== null) allScores.push(humanRiskScore);
  const overallScore = Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length);

  // Intelligent gap analysis based on lowest scores
  const allCategories = [
    { id: "manual", label: "Manual Workflow Dependence", score: operationalScore, desc: "Your team spends excessive time on tasks that should be automated, reducing capacity for strategic work.", opportunity: "Automate repetitive workflows to free up 15-20 hours per employee per month." },
    { id: "automation", label: "Automation Maturity Gap", score: automationScore, desc: "Disconnected systems and manual handoffs create bottlenecks, errors, and invisible delays.", opportunity: "Implement intelligent workflow automation across your highest-volume processes." },
    { id: "ai", label: "Uncontrolled AI Usage", score: aiScore, desc: "Employees are using AI tools without governance, risking data leakage and compliance violations.", opportunity: "Deploy governed AI tools with guardrails - increasing productivity while controlling risk." },
    { id: "access", label: "Access Visibility Gaps", score: accessScore, desc: "Limited visibility into who accesses what data creates audit risk and undetected exposure.", opportunity: "Implement role-based access controls with automated audit trails across all systems." },
    { id: "continuity", label: "Untested Recovery Plans", score: continuityScore, desc: "If systems went down today, recovery timelines are uncertain or untested.", opportunity: "Build and test automated disaster recovery that restores operations in hours, not days." },
    { id: "human", label: "Human Decision-Making Risk", score: humanRiskScore ?? 50, desc: "Team members may not recognize sophisticated AI-generated threats, creating entry points for attackers.", opportunity: "Implement behavioral awareness training with simulated real-world threat scenarios." },
  ];

  const topGaps = [...allCategories].sort((a, b) => a.score - b.score).slice(0, 3);
  const topOpportunities = [...allCategories].sort((a, b) => a.score - b.score).slice(0, 3);

  const roi = calculateAssessmentROI(answers, automationScore);

  const selectAnswer = (questionId, option) => {
    const value = typeof option === "string" ? { text: option } : option;
    setAnswers({ ...answers, [questionId]: value });
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setAnimating(true);
      setTimeout(() => { setCurrentStep(currentStep + 1); setAnimating(false); }, 250);
    } else {
      setAnimating(true);
      setTimeout(() => { setStage("contact"); setAnimating(false); }, 250);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setAnimating(true);
      setTimeout(() => { setCurrentStep(currentStep - 1); setAnimating(false); }, 200);
    }
  };

  const canProceed = steps[currentStep].questions.every(q => {
    if (answers[q.id] === undefined) return false;
    if (q.id === "industry" && answers.industry?.text === "Other") return !!answers.other_industry?.text?.trim();
    return true;
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = {
      company: fd.get("company"),
      name: fd.get("name"),
      phone: fd.get("phone"),
      email: fd.get("email"),
      source_page: "ai-business-assessment",
      situation: fd.get("situation") || "",
      contact_preference: fd.get("contact_preference") || "call",
    };
    setContactInfo(data);
    await submitLead(data);
    setStage("results");
    if (window.gtag) window.gtag("event", "assessment_complete", {
      event_category: "ai_assessment",
      overall_score: overallScore,
      automation_score: automationScore,
      ai_score: aiScore,
      risk_score: riskScore,
    });
  };

  const subScoreItems = [
    { score: automationScore, label: "Automation Maturity", icon: Zap },
    { score: aiScore, label: "AI Readiness", icon: Brain },
    { score: riskScore, label: "Operational Risk", icon: Shield },
    { score: humanRiskScore ?? 50, label: humanRiskScore !== null ? "Human Risk" : "Human Risk (est.)", icon: Eye },
    { score: continuityScore, label: "Business Continuity", icon: Clock },
    { score: operationalScore, label: "Operational Efficiency", icon: BarChart3 },
  ];

  const buildPDFPayload = () => ({
    companyName: contactInfo.company,
    overallScore,
    scoreLabel: getScoreLabel(overallScore).label,
    subScores: subScoreItems.map(({ label, score }) => ({ label, score })),
    topGaps,
    topOpportunities,
    roi,
  });

  const handleDownloadReport = () => {
    generateAssessmentPDF(buildPDFPayload());
    if (window.gtag) window.gtag("event", "assessment_report_download", { event_category: "ai_assessment", overall_score: overallScore });
  };

  const handleEmailReport = async () => {
    setEmailingReport(true);
    setReportEmailStatus(null);
    try {
      const pdfBase64 = getAssessmentPDFBase64(buildPDFPayload());
      await emailReport({
        leadId,
        reportToken,
        recipientEmail: contactInfo.email,
        recipientName: contactInfo.name,
        companyName: contactInfo.company,
        reportTitle: "Executive ROI & Readiness Report",
        pdfBase64,
      });
      setReportEmailStatus("sent");
      if (window.gtag) window.gtag("event", "assessment_report_email", { event_category: "ai_assessment", overall_score: overallScore });
    } catch (err) {
      console.error("Failed to email assessment report:", err);
      setReportEmailStatus("error");
    } finally {
      setEmailingReport(false);
    }
  };

  return (
    <section
      id="audit"
      data-testid="audit-section"
      aria-label="Business Technology Assessment"
      className="py-12 lg:py-18 bg-[#0f1d32]"
    >
      <div className="max-w-4xl mx-auto px-6">
        {stage === "intro" && (
          <AssessmentIntro
            steps={steps}
            completedCount={completedCount}
            onStart={() => { setStage("assess"); if (window.gtag) window.gtag("event", "assessment_start", { event_category: "ai_assessment" }); }}
          />
        )}

        {stage === "assess" && (
          <AssessmentSteps
            steps={steps}
            currentStep={currentStep}
            animating={animating}
            answers={answers}
            selectAnswer={selectAnswer}
            canProceed={canProceed}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        )}

        {stage === "contact" && (
          <AssessmentContact onSubmit={handleContactSubmit} />
        )}

        {stage === "results" && (
          <AssessmentResults
            error={error}
            overallScore={overallScore}
            roi={roi}
            contactInfo={contactInfo}
            handleDownloadReport={handleDownloadReport}
            handleEmailReport={handleEmailReport}
            emailingReport={emailingReport}
            reportEmailStatus={reportEmailStatus}
            subScoreItems={subScoreItems}
            topGaps={topGaps}
            topOpportunities={topOpportunities}
            allCategories={allCategories}
            humanRiskScore={humanRiskScore}
          />
        )}
      </div>
    </section>
  );
}
